// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * The guides of "Sky above me" (owner, 2026-10-22, after a first try on the
 * Quest: "I didn't see anything … maybe add guidelines to where the
 * satellite is"):
 *
 * - **the prompt**: the step the sky waits for (where are you, face north)
 *   and then a short summary, on a small panel in front of the viewer. It
 *   stays where it is while the head turns a little, and comes back in
 *   front when they look well away (a "lazy follow");
 * - **the pointer**: an arrow at the edge of the view toward the chosen
 *   satellite (the ISS at first), with its name, height and compass point,
 *   or when it rises if it is under the horizon;
 * - **drop lines**: from the horizon straight up to each named satellite,
 *   so the eye can climb from the skyline to the dot;
 * - **the belt**: the arc where the geostationary satellites stand in this
 *   viewer's sky.
 *
 * The prompt and pointer belong to the scene (in front of the camera); the
 * lines belong to the sky dome.
 */
import type * as THREE from 'three'
import { markPanel, unmarkPanel } from './vrPanelLayout'

const PROMPT_W = 0.62
const PROMPT_PX = 1024
/** Turn away further than this and the prompt comes back in front. */
const FOLLOW_DEG = 32

export interface SkyPromptHandle {
  readonly mesh: THREE.Mesh
  /** Show `text` (lines split on \n) in front of the viewer, or nothing. */
  update(camera: THREE.Camera, text: string | null): void
  dispose(): void
}

export function createSkyPrompt(THREE_: typeof THREE): SkyPromptHandle {
  const canvas = document.createElement('canvas')
  canvas.width = PROMPT_PX
  canvas.height = 256
  const tex = new THREE_.CanvasTexture(canvas)
  tex.colorSpace = THREE_.SRGBColorSpace
  const mat = new THREE_.MeshBasicMaterial({ map: tex, transparent: true, depthTest: false, depthWrite: false })
  const mesh = new THREE_.Mesh(new THREE_.PlaneGeometry(PROMPT_W, PROMPT_W / 4), mat)
  // AYNI (owner, 2026-11-05): kept off the other panels (vrPanelLayout).
  markPanel(mesh, { width: PROMPT_W, height: PROMPT_W / 4 })
  mesh.renderOrder = 30
  mesh.visible = false
  mesh.name = 'sky-prompt'
  let shown = ''
  let placed = false
  const fwd = new THREE_.Vector3()
  const to = new THREE_.Vector3()
  const eye = new THREE_.Vector3()

  function draw(text: string): void {
    const g = canvas.getContext('2d')!
    const lines = text.split('\n').slice(0, 3)
    g.clearRect(0, 0, canvas.width, canvas.height)
    g.fillStyle = 'rgba(8, 12, 20, 0.86)'
    g.beginPath()
    g.roundRect(4, 4, canvas.width - 8, canvas.height - 8, 36)
    g.fill()
    g.strokeStyle = 'rgba(63, 230, 255, 0.7)'
    g.lineWidth = 4
    g.stroke()
    g.fillStyle = '#ffffff'
    g.textAlign = 'center'
    g.textBaseline = 'middle'
    const size = lines.length > 2 ? 40 : 46
    g.font = `600 ${size}px system-ui, sans-serif`
    const step = canvas.height / (lines.length + 1)
    lines.forEach((line, i) => {
      g.fillStyle = i === 0 ? '#ffffff' : 'rgba(220, 240, 255, 0.85)'
      g.fillText(line, canvas.width / 2, step * (i + 1), canvas.width - 60)
    })
    tex.needsUpdate = true
  }

  return {
    mesh,
    update(camera, text) {
      mesh.visible = !!text
      if (!text) {
        placed = false
        return
      }
      if (text !== shown) {
        shown = text
        draw(text)
      }
      camera.getWorldPosition(eye)
      camera.getWorldDirection(fwd)
      to.copy(mesh.position).sub(eye).normalize()
      const away = (Math.acos(Math.max(-1, Math.min(1, fwd.dot(to)))) * 180) / Math.PI
      if (!placed || away > FOLLOW_DEG) {
        placed = true
        // 1.3 m ahead, a little under the eye line.
        mesh.position.copy(eye).addScaledVector(fwd, 1.3)
        mesh.position.y -= 0.18
      }
      mesh.lookAt(eye)
    },
    dispose() {
      unmarkPanel(mesh)
      tex.dispose()
      mat.dispose()
      mesh.geometry.dispose()
    },
  }
}

export interface SkyPointerHandle {
  readonly group: THREE.Group
  /**
   * Point toward `target` (a world position), named by `label`, while it is
   * well off the middle of the view; null hides the arrow.
   */
  update(camera: THREE.Camera, target: THREE.Vector3 | null, label: string): void
  dispose(): void
}

/** How far off the view's centre the target must be before the arrow shows, degrees. */
const POINTER_DEG = 22

export function createSkyPointer(THREE_: typeof THREE): SkyPointerHandle {
  const group = new THREE_.Group()
  group.name = 'sky-pointer'
  group.visible = false
  // The arrow: a flat triangle pointing up its local +y, turned in the view plane.
  const arrowShape = new THREE_.Shape()
  arrowShape.moveTo(0, 0.05)
  arrowShape.lineTo(-0.032, -0.02)
  arrowShape.lineTo(0, -0.006)
  arrowShape.lineTo(0.032, -0.02)
  arrowShape.closePath()
  const arrowMat = new THREE_.MeshBasicMaterial({ color: 0x3fe6ff, transparent: true, opacity: 0.95, depthTest: false, depthWrite: false })
  const arrow = new THREE_.Mesh(new THREE_.ShapeGeometry(arrowShape), arrowMat)
  arrow.renderOrder = 31
  group.add(arrow)
  const canvas = document.createElement('canvas')
  canvas.width = 512
  canvas.height = 96
  const tex = new THREE_.CanvasTexture(canvas)
  tex.colorSpace = THREE_.SRGBColorSpace
  const labelMat = new THREE_.MeshBasicMaterial({ map: tex, transparent: true, depthTest: false, depthWrite: false })
  const label = new THREE_.Mesh(new THREE_.PlaneGeometry(0.3, 0.056), labelMat)
  label.renderOrder = 31
  group.add(label)
  let shown = ''
  const eye = new THREE_.Vector3()
  const fwd = new THREE_.Vector3()
  const up = new THREE_.Vector3()
  const right = new THREE_.Vector3()
  const to = new THREE_.Vector3()
  const q = new THREE_.Quaternion()

  function draw(text: string): void {
    const g = canvas.getContext('2d')!
    g.clearRect(0, 0, canvas.width, canvas.height)
    g.font = '600 38px system-ui, sans-serif'
    g.textAlign = 'center'
    g.textBaseline = 'middle'
    g.lineWidth = 8
    g.strokeStyle = 'rgba(5, 8, 14, 0.9)'
    g.strokeText(text, canvas.width / 2, canvas.height / 2, canvas.width - 20)
    g.fillStyle = '#e6faff'
    g.fillText(text, canvas.width / 2, canvas.height / 2, canvas.width - 20)
    tex.needsUpdate = true
  }

  return {
    group,
    update(camera, target, text) {
      if (!target) {
        group.visible = false
        return
      }
      camera.getWorldPosition(eye)
      camera.getWorldQuaternion(q)
      fwd.set(0, 0, -1).applyQuaternion(q)
      up.set(0, 1, 0).applyQuaternion(q)
      right.set(1, 0, 0).applyQuaternion(q)
      to.copy(target).sub(eye).normalize()
      const off = (Math.acos(Math.max(-1, Math.min(1, fwd.dot(to)))) * 180) / Math.PI
      group.visible = off > POINTER_DEG
      if (!group.visible) return
      if (text !== shown) {
        shown = text
        draw(text)
      }
      // The target's direction in the view plane, as an angle (0 = up).
      const x = to.dot(right)
      const y = to.dot(up)
      const angle = Math.atan2(-x, y)
      const r = 0.2
      group.position.copy(eye).addScaledVector(fwd, 0.9).addScaledVector(right, -Math.sin(angle) * r).addScaledVector(up, Math.cos(angle) * r)
      group.quaternion.copy(q)
      arrow.rotation.set(0, 0, angle)
      // The label sits on the arrow's inner side, toward the view's centre.
      label.position.set(Math.sin(angle) * 0.075, -Math.cos(angle) * 0.075, 0)
    },
    dispose() {
      arrow.geometry.dispose()
      arrowMat.dispose()
      label.geometry.dispose()
      labelMat.dispose()
      tex.dispose()
    },
  }
}

export interface SkyLinesHandle {
  readonly group: THREE.Group
  /** Drop lines from the horizon up to each of `points` (dome positions, NaN skipped). */
  setDrops(points: ReadonlyArray<[number, number, number]>): void
  /** The geostationary belt across this sky (dome positions, NaN = under the horizon). */
  setBelt(points: Float32Array): void
  dispose(): void
}

export function createSkyLines(THREE_: typeof THREE): SkyLinesHandle {
  const group = new THREE_.Group()
  group.name = 'sky-lines'
  const dropMat = new THREE_.LineBasicMaterial({ color: 0xe6faff, transparent: true, opacity: 0.55, depthTest: false, depthWrite: false })
  const beltMat = new THREE_.LineDashedMaterial({ color: 0xffb020, transparent: true, opacity: 0.6, dashSize: 0.6, gapSize: 0.4, depthTest: false, depthWrite: false })
  const drops = new THREE_.LineSegments(new THREE_.BufferGeometry(), dropMat)
  const belt = new THREE_.LineSegments(new THREE_.BufferGeometry(), beltMat)
  for (const l of [drops, belt]) {
    l.frustumCulled = false
    l.renderOrder = 6
    group.add(l)
  }
  let dropKey = ''

  return {
    group,
    setDrops(points) {
      const v: number[] = []
      for (const [x, y, z] of points) {
        if (x !== x || y <= 0) continue
        // From the skyline under the satellite (same bearing, height 0) up to it.
        const h = Math.hypot(x, z)
        const r = Math.hypot(x, y, z)
        if (h <= 0) continue
        v.push((x / h) * r, 0, (z / h) * r, x, y, z)
      }
      const key = v.map((n) => n.toFixed(2)).join(',')
      if (key === dropKey) return
      dropKey = key
      drops.geometry.dispose()
      drops.geometry = new THREE_.BufferGeometry()
      drops.geometry.setAttribute('position', new THREE_.BufferAttribute(new Float32Array(v), 3))
    },
    setBelt(points) {
      const v: number[] = []
      const n = points.length / 3
      for (let i = 0; i < n; i++) {
        const a = i * 3
        const b = ((i + 1) % n) * 3
        if (points[a] !== points[a] || points[b] !== points[b]) continue
        v.push(points[a], points[a + 1], points[a + 2], points[b], points[b + 1], points[b + 2])
      }
      belt.geometry.dispose()
      belt.geometry = new THREE_.BufferGeometry()
      belt.geometry.setAttribute('position', new THREE_.BufferAttribute(new Float32Array(v), 3))
      belt.computeLineDistances()
    },
    dispose() {
      drops.geometry.dispose()
      belt.geometry.dispose()
      dropMat.dispose()
      beltMat.dispose()
    },
  }
}
